import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('Admin');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // First, check if this email belongs to multiple tenants
      const tenantsResponse = await axios.post(`${API}/auth/check-email-tenants`, { email });
      const tenants = tenantsResponse.data.tenants || [];

      // If user belongs to multiple tenants, show selection modal
      if (tenants.length > 1) {
        setAvailableTenants(tenants);
        setShowTenantModal(true);
        setLoading(false);
        return;
      }

      // If single tenant or no tenant, proceed with normal login
      // (backend will handle the case where no tenant is found)
      const company_id = tenants.length === 1 ? tenants[0].company_id : null;
      await performLogin(company_id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
      setLoading(false);
    }
  };

  const performLogin = async (company_id = null) => {
    try {
      // Normalize role to lowercase for backend API
      const normalizedRole = role.toLowerCase().replace(/\s+/g, '_');
      const loginData = {
        email,
        password,
        role: normalizedRole
      };

      // Add company_id if provided (for multi-tenant selection)
      if (company_id) {
        loginData.company_id = company_id;
      }

      const response = await axios.post(`${API}/auth/login`, loginData);

      // Clear any existing platform admin tokens to prevent conflicts
      localStorage.removeItem('platform_admin_token');
      localStorage.removeItem('platform_admin');

      // Check if password reset is required
      if (response.data.user.password_reset_required) {
        // Store user data and token temporarily for password change
        localStorage.setItem('temp_token', response.data.access_token);
        localStorage.setItem('temp_user', JSON.stringify(response.data.user));
        navigate('/change-password');
      } else {
        // Normal login flow - update both App state and AuthContext
        onLogin(response.data.user, response.data.access_token);
        authLogin(response.data.access_token, response.data.user);
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleTenantSelection = async (tenant) => {
    setSelectedTenant(tenant);
    setShowTenantModal(false);
    setLoading(true);

    try {
      await performLogin(tenant.company_id);
    } catch (err) {
      // Error already handled in performLogin
      setShowTenantModal(true); // Show modal again if login failed
    }
  };

  const handleCancelTenantSelection = () => {
    setShowTenantModal(false);
    setAvailableTenants([]);
    setSelectedTenant(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4" data-testid="login-page">
      {/* Tenant Selection Modal */}
      {showTenantModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Select Your Organization</h2>
            <p className="text-gray-600 mb-6">Your account is associated with multiple organizations. Please select which one you want to log into:</p>

            <div className="space-y-3 mb-6">
              {availableTenants.map((tenant) => (
                <button
                  key={tenant.company_id}
                  onClick={() => handleTenantSelection(tenant)}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-[#1B5C94] hover:bg-blue-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                >
                  <div className="font-semibold text-gray-800">{tenant.company_name}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Role: {tenant.user_role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">User: {tenant.user_name}</div>
                </button>
              ))}
            </div>

            <button
              onClick={handleCancelTenantSelection}
              className="w-full py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img
              src="/saarv-logo.svg"
              alt="Saarv Logo"
              className="h-20 w-auto"
            />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] bg-clip-text text-transparent mb-2">SAARV PLATFORM</h2>
          <p className="text-[#1B5C94] font-semibold">Safety | Energy | Resources | Sustainability</p>
        </div>

        <form onSubmit={handleSubmit} data-testid="login-form">
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="role">
              Login As <span className="text-red-500">*</span>
            </label>
            <select
              data-testid="role-select"
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent bg-white"
              required
            >
              <optgroup label="System Roles">
                <option value="Admin">Admin</option>
                <option value="HSE Manager">HSE Manager</option>
              </optgroup>
              <optgroup label="Safety Roles">
                <option value="Safety Manager">Safety Manager</option>
                <option value="Safety Officer">Safety Officer</option>
                <option value="Safety Coordinator">Safety Coordinator</option>
              </optgroup>
              <optgroup label="Operations Roles">
                <option value="Supervisor">Supervisor</option>
                <option value="Operator">Operator</option>
                <option value="Technician">Technician</option>
                <option value="Engineer">Engineer</option>
              </optgroup>
              <optgroup label="Management Roles">
                <option value="Manager">Manager</option>
                <option value="Director">Director</option>
              </optgroup>
              <optgroup label="Support Roles">
                <option value="Contractor">Contractor</option>
                <option value="Reporter">Reporter</option>
              </optgroup>
              <optgroup label="Legacy Roles (Deprecated)">
                <option value="admin">Administrator (Old)</option>
                <option value="hse_manager">HSE Manager (Old)</option>
                <option value="reporter">Reporter (Old)</option>
              </optgroup>
            </select>
            <p className="text-xs text-gray-500 mt-1">Select your role to login</p>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="email">
              Email
            </label>
            <input
              data-testid="email-input"
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="password">
              Password
            </label>
            <input
              data-testid="password-input"
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              required
            />
            <div className="text-right mt-2">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-[#1B5C94] hover:text-[#3AC0DB] font-semibold transition-colors"
              >
                Forgot Password?
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm" data-testid="error-message">
              {error}
            </div>
          )}

          <button
            data-testid="login-button"
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white font-bold py-3 px-4 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Logging in...' : 'LOGIN'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Need access? Contact your administrator or</p>
          <a href="mailto:sales@saarv.in" className="text-[#1B5C94] hover:text-[#3AC0DB] font-semibold">
            sales@saarv.in
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;